// SubCategorySelectionModal.tsx
import React from "react";
import { X } from "lucide-react";
import { getCategoryIcon } from "../../utils/getCategoryIcon";

interface SubCategory {
  id: string;
  name: string;
  name_th: string;
  name_es: string;
  name_id?: string;
  name_ar?: string;
  description?: string;
  description_th?: string;
  description_es?: string;
  description_id?: string;
  description_ar?: string;
  icon: string;
  color?: string;
}

interface SubCategorySelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (subCategoryId: string, categoryName: string) => void;
  categoryName: string;
  subCategories: SubCategory[];
}

const SubCategorySelectionModal: React.FC<SubCategorySelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  categoryName,
  subCategories,
}) => {
  if (!isOpen) return null;

  const userInfo = localStorage.getItem("user");
  const myLang = userInfo ? JSON.parse(userInfo) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Choose Your Astrological Reading
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose a specific area within "{categoryName}"
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* SubCategories List */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {subCategories.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No subcategories available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {subCategories.map((subCategory) => {
                const Icon = getCategoryIcon(subCategory.icon);
                const color = subCategory.color || "#3B82F6"; // Default blue

                return (
                  <button
                    key={subCategory.id}
                    onClick={() => onSelect(subCategory.id, subCategory.name)}
                    className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-primary hover:bg-primary/5 transition-all duration-200 hover:shadow-sm group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110"
                        style={{
                          backgroundColor: `${color}15`,
                          border: `2px solid ${color}30`,
                        }}
                      >
                        <Icon
                          size={24}
                          style={{ color: color }}
                          className="transition-transform group-hover:scale-110"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3
                          className="font-medium text-gray-900 text-sm"
                          style={{ color: color }}
                        >
                          {/* {subCategory.name} */}
                          {myLang?.preferredLanguage === "en"
                            ? subCategory.name
                            : myLang?.preferredLanguage === "th"
                              ? subCategory.name_th || subCategory.name
                              : myLang?.preferredLanguage === "es"
                                ? subCategory.name_es || subCategory.name
                                : myLang?.preferredLanguage === "id"
                                  ? subCategory.name_id || subCategory.name
                                  : myLang?.preferredLanguage === "ar"
                                    ? subCategory.name_ar || subCategory.name
                                    : subCategory.name}
                        </h3>
                        {subCategory.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {/* {subCategory.description} */}
                            {myLang?.preferredLanguage === "en"
                              ? subCategory.description
                              : myLang?.preferredLanguage === "th"
                                ? subCategory.description_th ||
                                  subCategory.description
                                : myLang?.preferredLanguage === "es"
                                  ? subCategory.description_es ||
                                    subCategory.description
                                  : myLang?.preferredLanguage === "id"
                                    ? subCategory.description_id ||
                                      subCategory.description
                                    : myLang?.preferredLanguage === "ar"
                                      ? subCategory.description_ar ||
                                        subCategory.description
                                      : subCategory.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubCategorySelectionModal;
